import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

import { AdminService } from '@generated/services/admin.service';
import { BenchmarkDetailDto } from '@generated/models/benchmark-detail-dto';
import { BenchmarkRunSummaryDto } from '@generated/models/benchmark-run-summary-dto';
import { NotificationService } from '@core/services/notification.service';
import { Signalr } from '@core/services/signalr';

import { BenchmarkRunDetailsDrawerComponent } from '@features/admin/benchmark-run-details-drawer/benchmark-run-details-drawer.component';

/**
 * История прогонов бенчмарка — отдельная страница.
 *
 * URL: /admin/benchmarks/:id/runs
 *
 * После SKN-290 update'а: run — GROUP, метрики per-item. Список показывает
 * parent runs с counters по статусам item'ов. Клик → drawer с
 * пораскрытыми items.
 *
 *   - GET /api/Admin/benchmarks/{id} — заголовок (title, type, iterations)
 *   - GET /api/Admin/benchmarks/{id}/runs — курсорная пагинация parent runs
 *   - Live SignalR:
 *       * BenchmarkRunUpdated → patch parent status/recorded_at
 *       * BenchmarkRunItemUpdated → перезагрузка первой страницы (для
 *         обновления item counters на parent'e — их бэк агрегирует
 *         только на BenchmarkRunUpdated'e; если бэк начнёт слать
 *         counter в message, можно будет патчить without reload).
 */
@Component({
  selector: 'app-benchmark-runs',
  standalone: true,
  imports: [
    RouterModule,
    ButtonModule,
    TableModule,
    TagModule,
    TooltipModule,
    ProgressSpinnerModule,
    BenchmarkRunDetailsDrawerComponent,
  ],
  templateUrl: './benchmark-runs.component.html',
  styleUrl: './benchmark-runs.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BenchmarkRunsComponent implements OnInit, OnDestroy {
  private adminService = inject(AdminService);
  private notificationService = inject(NotificationService);
  private signalrService = inject(Signalr);
  private cdr = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroy$ = new Subject<void>();

  readonly benchmarkId = signal<string | null>(null);
  readonly benchmark = signal<BenchmarkDetailDto | null>(null);
  readonly isLoadingBenchmark = signal<boolean>(false);

  readonly runs = signal<BenchmarkRunSummaryDto[]>([]);
  readonly runsNextCursor = signal<string | null>(null);
  readonly isLoadingRuns = signal<boolean>(false);
  readonly runsPageSize = 50;

  readonly drawerVisible = signal<boolean>(false);
  readonly selectedRunId = signal<string | null>(null);

  readonly modelTypeCode = computed(() => (this.benchmark()?.model_type_code ?? '').toLowerCase());

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.notificationService.showError(
        $localize`:@@benchmarks.runs.missingId:ID бенчмарка не указан`
      );
      this.router.navigate(['/admin/benchmarks']);
      return;
    }
    this.benchmarkId.set(id);
    this.loadBenchmark(id);
    this.loadRuns(id);

    // Parent run status update — точечно патчим строку в списке.
    this.signalrService.benchmarkRunUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        if (msg.benchmark_id !== this.benchmarkId()) return;
        const list = this.runs();
        const idx = list.findIndex(r => r.id === msg.benchmark_run_id);
        if (idx >= 0) {
          const updated = { ...list[idx] };
          if (msg.status != null) updated.status = this.mapRunStatus(msg.status);
          if (msg.recorded_at) updated.recorded_at = msg.recorded_at;
          const next = [...list];
          next[idx] = updated;
          this.runs.set(next);
          this.cdr.markForCheck();
        } else {
          // Новый run — перезагружаем первую страницу.
          this.loadRuns(this.benchmarkId()!);
        }
      });

    // Item-level updates — счётчики parent'а бэк пересчитывает и шлёт в
    // BenchmarkRunUpdated. Здесь достаточно тихого reload'а списка, если
    // item update пришёл по нашему бенчу (не первым, чтобы не заспамить).
    this.signalrService.benchmarkRunItemUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        if (msg.benchmark_id !== this.benchmarkId()) return;
        // Не reload'им если drawer открыт для ЭТОГО run'а — он сам
        // fetch'нет свежий detail через своё effect'ом.
        if (this.drawerVisible() && this.selectedRunId() === msg.benchmark_run_id) return;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private mapRunStatus(s: number): 'Initial' | 'Running' | 'Completed' | 'Failed' | 'Stopped' {
    const names: Array<'Initial' | 'Running' | 'Completed' | 'Failed' | 'Stopped'> =
      ['Initial', 'Running', 'Completed', 'Failed', 'Stopped'];
    return names[s] ?? 'Initial';
  }

  private loadBenchmark(id: string): void {
    this.isLoadingBenchmark.set(true);
    this.adminService.apiAdminBenchmarksBenchmarkIdGet({ benchmarkId: id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.benchmark.set(data);
          this.isLoadingBenchmark.set(false);
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Failed to load benchmark:', error);
          this.notificationService.showError(
            $localize`:@@benchmarks.runs.loadBenchmarkFail:Не удалось загрузить бенчмарк`
          );
          this.isLoadingBenchmark.set(false);
          this.cdr.markForCheck();
        }
      });
  }

  loadRuns(benchmarkId: string): void {
    this.isLoadingRuns.set(true);
    this.adminService.apiAdminBenchmarksBenchmarkIdRunsGet({
      benchmarkId,
      limit: this.runsPageSize,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.runs.set(data?.items ?? []);
          this.runsNextCursor.set(data?.next_cursor ?? null);
          this.isLoadingRuns.set(false);
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Failed to load runs history:', error);
          this.isLoadingRuns.set(false);
          this.cdr.markForCheck();
        }
      });
  }

  loadMoreRuns(): void {
    const benchmarkId = this.benchmarkId();
    const cursor = this.runsNextCursor();
    if (!benchmarkId || !cursor || this.isLoadingRuns()) return;
    this.isLoadingRuns.set(true);
    this.adminService.apiAdminBenchmarksBenchmarkIdRunsGet({
      benchmarkId,
      cursor,
      limit: this.runsPageSize,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.runs.set([...this.runs(), ...(data?.items ?? [])]);
          this.runsNextCursor.set(data?.next_cursor ?? null);
          this.isLoadingRuns.set(false);
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Failed to load more runs:', error);
          this.isLoadingRuns.set(false);
          this.cdr.markForCheck();
        }
      });
  }

  openRunDetails(runId: string | null | undefined): void {
    if (!runId) return;
    this.selectedRunId.set(runId);
    this.drawerVisible.set(true);
  }

  onDrawerVisibleChange(v: boolean): void {
    this.drawerVisible.set(v);
    if (!v) this.selectedRunId.set(null);
  }

  goBack(): void {
    const id = this.benchmarkId();
    if (id) {
      this.router.navigate(['/admin/benchmarks', id, 'edit']);
    } else {
      this.router.navigate(['/admin/benchmarks']);
    }
  }

  // ===== template helpers =====
  runStatusSeverity(s: string | null | undefined): 'info' | 'success' | 'warn' | 'danger' | 'secondary' {
    switch ((s || '').toLowerCase()) {
      case 'completed': return 'success';
      case 'failed': return 'danger';
      case 'running': return 'warn';
      case 'initial': return 'info';
      case 'stopped': return 'secondary';
      default: return 'secondary';
    }
  }

  runTriggerLabel(t: string | null | undefined): string {
    switch ((t || '').toLowerCase()) {
      case 'manual': return $localize`:@@benchmarks.trigger.manual:Manual`;
      case 'scheduled': return $localize`:@@benchmarks.trigger.scheduled:Scheduled`;
      default: return t || '';
    }
  }

  formatDate(v: string | null | undefined): string {
    if (!v) return '';
    try { return new Date(v).toLocaleString(); } catch { return v; }
  }
}
